<template>
  <!-- 병원/기공소 공유 목록 컴포넌트 — SPEC-CHINA-ADMIN-HOSPITAL-001 REQ-UBI-004 -->
  <!-- type prop으로 HOSPITAL/DENTAL_LABORATORY 두 화면 재사용 -->
  <div class="page-container-v2">
    <!-- 페이지 헤더 + 축 안내 — 임베드(실무자 계정 관리 탭)에서는 숨긴다.
         바깥 화면이 자기 제목·안내를 이미 갖고 있어 중복 노출되기 때문(readOnly=임베드 신호). -->
    <template v-if="!readOnly">
      <div class="page-header">
        <span class="page-title">{{ pageTitle }}</span>
      </div>
      <a-alert type="info" show-icon style="margin-bottom: 12px;">
        <template #message>
          {{ type === 'HOSPITAL' ? $t('company_axis_note_hospital') : $t('company_axis_note_dentalLab') }}
          <router-link to="/mfr-staff-manage" style="margin-left: 8px;">{{ $t('company_axis_link_staff') }}</router-link>
        </template>
      </a-alert>
    </template>

    <!-- 필터 카드 — REQ-LIST-001 -->
    <a-card style="margin-bottom: 16px;">
      <a-form layout="inline" class="company-filter-form" :model="filters" @finish="onSearch">
        <!-- 지역 (addrOne) -->
        <a-form-item :label="$t('company_filter_addrOne')">
          <a-input
            v-model:value="filters.addrOne"
            :placeholder="$t('company_filter_addrOne_placeholder')"
            style="width: 140px;"
            allow-clear
          />
        </a-form-item>

        <!-- 진료과 (medicalDept) — 자유 입력 (OD-FE-5) -->
        <a-form-item :label="$t('company_filter_medicalDept')">
          <a-input
            v-model:value="filters.medicalDept"
            :placeholder="$t('company_filter_medicalDept_placeholder')"
            style="width: 140px;"
            allow-clear
          />
        </a-form-item>

        <!-- 등록 상태 (registrationStatus) -->
        <a-form-item :label="$t('company_filter_registrationStatus')">
          <a-select
            v-model:value="filters.registrationStatus"
            :placeholder="$t('company_filter_registrationStatus_placeholder')"
            style="width: 140px;"
            allow-clear
          >
            <a-select-option value="PENDING">{{ $t('company_status_PENDING') }}</a-select-option>
            <a-select-option value="APPROVED">{{ $t('company_status_APPROVED') }}</a-select-option>
            <a-select-option value="REJECTED">{{ $t('company_status_REJECTED') }}</a-select-option>
            <a-select-option value="SUSPENDED">{{ $t('company_status_SUSPENDED') }}</a-select-option>
          </a-select>
        </a-form-item>

        <!-- 테스트 포함 (isTestCompany) -->
        <a-form-item :label="$t('company_filter_isTestCompany')">
          <a-select
            v-model:value="filters.isTestCompany"
            :placeholder="$t('company_filter_all')"
            style="width: 120px;"
            allow-clear
          >
            <a-select-option value="true">{{ $t('company_filter_test_include') }}</a-select-option>
            <a-select-option value="false">{{ $t('company_filter_test_exclude') }}</a-select-option>
          </a-select>
        </a-form-item>

        <!-- 이름 검색 -->
        <a-form-item :label="$t('company_filter_name')">
          <a-input
            v-model:value="filters.name"
            :placeholder="$t(`company_filter_name_placeholder${entitySuffix}`)"
            style="width: 160px;"
            allow-clear
          />
        </a-form-item>

        <a-form-item class="filter-actions">
          <a-space>
            <a-button type="primary" html-type="submit" :loading="loading">
              {{ $t('company_btn_search') }}
            </a-button>
            <a-button @click="onReset">
              {{ $t('company_btn_reset') }}
            </a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 에러 표시 — REQ-AUTH-002 -->
    <a-alert
      v-if="errorMsg"
      type="error"
      :message="errorMsg"
      style="margin-bottom: 16px;"
      show-icon
      closable
      @close="errorMsg = ''"
    />

    <!-- 툴바: 총건수 / 정렬 / limit / 추가 버튼 -->
    <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px; flex-wrap: wrap;">
      <!-- 총건수는 좌측, 정렬/페이지/추가는 우측으로 분리 -->
      <span style="margin-right: auto;">{{ $t('company_total_count', { count: pagination.total.toLocaleString() }) }}</span>

      <!-- 추가 버튼 — 우측 그룹 맨 앞(정렬 드롭박스 좌측). 권한 매트릭스 §5 (REQ-AUTH-001) -->
      <!-- DENTAL_LAB는 기공소 추가 불가 (REQ-UNW-008, REQ-STATE-001) -->
      <a-button
        v-if="canCreate"
        type="primary"
        @click="openFormModal(null)"
      >
        + {{ $t(`company_form_title_add${entitySuffix}`) }}
      </a-button>

      <!-- 정렬 whitelist — REQ-LIST-003, REQ-UNW-003 -->
      <a-select
        v-model:value="sortBy"
        style="width: 120px;"
        @change="onSortChange"
      >
        <a-select-option value="createAt">{{ $t('company_sort_createAt') }}</a-select-option>
        <a-select-option value="name">{{ $t('company_sort_name') }}</a-select-option>
        <a-select-option value="modifyAt">{{ $t('company_sort_modifyAt') }}</a-select-option>
        <!-- lastLoginAt 노출 금지 (REQ-UNW-003, EXC-4) -->
      </a-select>

      <a-select
        v-model:value="sortOrder"
        style="width: 90px;"
        @change="onSortChange"
      >
        <a-select-option value="desc">{{ $t('company_sort_desc') }}</a-select-option>
        <a-select-option value="asc">{{ $t('company_sort_asc') }}</a-select-option>
      </a-select>

      <!-- 페이지당 건수 -->
      <a-select
        v-model:value="pageSize"
        style="width: 100px;"
        @change="onPageSizeChange"
      >
        <a-select-option :value="10">10</a-select-option>
        <a-select-option :value="20">20</a-select-option>
        <a-select-option :value="50">50</a-select-option>
        <a-select-option :value="100">100</a-select-option>
      </a-select>
    </div>

    <!-- 데스크탑 테이블 (@media > 767px) -->
    <div v-if="!isMobileView">
      <a-table
        :data-source="companies"
        :columns="tableColumns"
        :loading="loading"
        :pagination="false"
        row-key="_id"
        size="small"
      >
        <template #bodyCell="{ column, record }">
          <!-- 제조사 chips (viewManufacturers) — REQ-LIST-005 -->
                        <template v-if="column.key === 'accountCount'">
                <a-button type="link" size="small" style="padding:0" @click="openAccountModal(record)">
                  {{ (record.accountInfo || []).length }}
                </a-button>
              </template>
              <template v-if="column.key === 'viewManufacturers'">
            <a-tag
              v-for="mfr in (record.viewManufacturers || [])"
              :key="mfr._id"
              color="blue"
            >
              {{ mfr.name || mfr._id }}
            </a-tag>
            <span v-if="!(record.viewManufacturers || []).length">-</span>
          </template>

          <!-- 기공소 chips (viewDentalLabs — 병원만) — REQ-LIST-005 -->
          <template v-if="column.key === 'viewDentalLabs' && type === 'HOSPITAL'">
            <a-tag
              v-for="lab in (record.viewDentalLabs || [])"
              :key="lab._id"
              color="green"
            >
              {{ lab.name || lab._id }}
            </a-tag>
            <span v-if="!(record.viewDentalLabs || []).length">-</span>
          </template>

          <!-- 영업집조(营业执照) — 이미지 클릭 미리보기.
               영업집조 번호는 cNo(统一社会信用代码)와 동일하므로 별도 표시 없음 -->
          <template v-if="column.key === 'businessLicense'">
            <div class="business-license-cell">
              <a-image
                v-if="record.businessLicenseImageUrl"
                :src="record.businessLicenseImageUrl"
                :width="40"
                :height="40"
                :preview="{ mask: $t('company_form_businessLicense_preview') }"
                style="border: 1px solid #eee; border-radius: 4px; object-fit: cover;"
              />
              <span v-else class="license-no-img">{{ $t('company_col_businessLicense_none') }}</span>
            </div>
          </template>

          <!-- 헝지우(恒久) 연동 여부 — 기공소 전용 -->
          <template v-if="column.key === 'hengjiu'">
            <a-tag v-if="record.hengjiuFactoryId" color="purple">
              <a-tooltip :title="record.hengjiuFactoryId" placement="topLeft">
                {{ $t('company_col_hengjiu_on') }}
              </a-tooltip>
            </a-tag>
            <!-- 제출은 됐지만 서명 검증 전 — '미연동'과 섞이면 대기 중인 기공소를 찾을 수 없다 -->
            <a-tag v-else-if="record.hengjiuState === 'pending'" color="orange">
              <a-tooltip
                :title="$t('company_col_hengjiu_pending_tip', { factoryId: record.hengjiuPendingFactoryId || '-' })"
                placement="topLeft"
              >
                {{ $t('company_col_hengjiu_pending') }}
              </a-tooltip>
            </a-tag>
            <span v-else style="color: #ccc;">—</span>
          </template>

          <!-- 등록일 -->
          <template v-if="column.key === 'createAt'">
            {{ formatDate(record.createAt) }}
          </template>

          <!-- 행 액션 -->
          <template v-if="column.key === 'actions'">
            <a-space>
              <!-- 수정 버튼 — XINLAIAN/MANUFACTURER는 표시, DENTAL_LAB는 병원만 (§5) -->
              <a-button
                v-if="canEdit(record)"
                size="small"
                @click="openFormModal(record)"
              >
                {{ $t('company_btn_edit') }}
              </a-button>

              <!-- 계정관리 — 권한 있는 경우 표시 (§5) -->
              <a-button
                v-if="canManageAccounts"
                size="small"
                @click="openAccountModal(record)"
              >
                {{ $t('company_btn_accounts') }}
              </a-button>

              <!-- ID복사 — REQ-ACT-003, REQ-UNW-004 (_id는 컬럼 미렌더) -->
              <a-button size="small" @click="copyId(record)">
                {{ $t('company_btn_copy_id') }}
              </a-button>

              <!-- 삭제 — 생성 권한과 동일 범위(제조사→기공소 / 기공소→병원 / 운영자→전부) -->
              <a-button
                v-if="canDelete"
                size="small"
                danger
                @click="onDelete(record)"
              >
                {{ $t('company_btn_delete') }}
              </a-button>
            </a-space>
          </template>
        </template>
      </a-table>
    </div>

    <!-- 모바일 카드뷰 (REQ-STATE-005, @media max-width:767px) -->
    <div v-else class="mobile-card-list">
      <div
        v-for="record in companies"
        :key="record._id"
        class="mobile-company-card"
      >
        <div class="card-field">
          <span class="card-label">{{ $t('company_col_name') }}:</span>
          <span>{{ record.name }}</span>
        </div>
        <div class="card-field">
          <span class="card-label">{{ $t('company_col_addrOne') }}:</span>
          <span>{{ record.addrOne || '-' }}</span>
        </div>
        <div class="card-field">
          <span class="card-label">{{ $t('company_col_registrationStatus') }}:</span>
          <span>{{ record.registrationStatus ? ($te(`company_status_${record.registrationStatus}`) ? $t(`company_status_${record.registrationStatus}`) : record.registrationStatus) : '-' }}</span>
        </div>
        <!-- 영업집조(营业执照) — 모바일 (이미지만, 번호는 cNo가 담당) -->
        <div class="card-field" v-if="record.businessLicenseImageUrl">
          <span class="card-label">{{ $t('company_col_businessLicense') }}:</span>
          <div class="business-license-cell">
            <a-image
              :src="record.businessLicenseImageUrl"
              :width="48"
              :height="48"
              :preview="{ mask: $t('company_form_businessLicense_preview') }"
              style="border: 1px solid #eee; border-radius: 4px; object-fit: cover;"
            />
          </div>
        </div>
        <!-- 제조사 chips (모바일) -->
        <div class="card-field" v-if="(record.viewManufacturers || []).length">
          <span class="card-label">{{ $t('company_col_viewManufacturers') }}:</span>
          <a-tag
            v-for="mfr in record.viewManufacturers"
            :key="mfr._id"
            color="blue"
          >{{ mfr.name || mfr._id }}</a-tag>
        </div>
        <!-- 모바일 행 액션 -->
        <div class="card-actions">
          <a-button
            v-if="canEdit(record)"
            size="small"
            @click="openFormModal(record)"
          >{{ $t('company_btn_edit') }}</a-button>
          <a-button
            v-if="canManageAccounts"
            size="small"
            @click="openAccountModal(record)"
          >{{ $t('company_btn_accounts') }}</a-button>
          <a-button size="small" @click="copyId(record)">
            {{ $t('company_btn_copy_id') }}
          </a-button>
          <a-button
            v-if="canDelete"
            size="small"
            danger
            @click="onDelete(record)"
          >{{ $t('company_btn_delete') }}</a-button>
        </div>
      </div>
      <div v-if="!loading && companies.length === 0" style="text-align: center; color: #999; padding: 24px;">
        {{ $t('company_no_data') }}
      </div>
    </div>

    <!-- 페이지네이션 — REQ-LIST-002, envelope 소비 -->
    <div style="display: flex; justify-content: flex-end; margin-top: 16px;">
      <a-pagination
        v-model:current="pagination.current"
        :total="pagination.total"
        :page-size="pageSize"
        :show-total="(total) => $t('company_total_count', { count: total.toLocaleString() })"
        show-size-changer
        :page-size-options="['10', '20', '50', '100']"
        @change="onPageChange"
        @show-size-change="onPageSizeChange"
      />
    </div>
  </div>

  <!-- 추가/수정 모달 (OD-FE-1: 모달 채택) -->
  <CompanyFormModal
    :open="formModalOpen"
    :type="type"
    :record="selectedRecord"
    @close="closeFormModal"
    @saved="onSaved"
  />

  <!-- 계정관리 모달 -->
  <AccountManageModal
    :open="accountModalOpen"
    :type="type"
    :company="selectedRecord"
    @close="closeAccountModal"
  />
</template>

<script>
import CompanyFormModal from './components/CompanyFormModal.vue';
import AccountManageModal from './components/AccountManageModal.vue';
import { listCompanies, deleteCompany, parsePaginatedResponse, extractApiError } from '@/apis/chinaCompany';
import { confirmDialog, jwtRole } from '@/utils/utils';

// @MX:ANCHOR: [AUTO] CompanyList.vue — HOSPITAL/DENTAL_LABORATORY 두 화면의 공유 진입점
// @MX:REASON: [AUTO] router.js 2개 라우트가 이 컴포넌트를 type prop으로 재사용 (fan_in=2, EXC-9 패턴)
// @MX:SPEC: SPEC-CHINA-ADMIN-HOSPITAL-001

export default {
  name: 'CompanyList',

  components: {
    CompanyFormModal,
    AccountManageModal,
  },

  props: {
    // @MX:NOTE: [AUTO] REQ-UNW-006 — class/style을 props로 정의하지 않음
    /** HOSPITAL | DENTAL_LABORATORY */
    type: {
      type: String,
      required: true,
      validator: (v) => ['HOSPITAL', 'DENTAL_LABORATORY'].includes(v),
    },
    /**
     * 읽기 전용 모드 — SPEC-CHINA-MFR-STAFF-ACCOUNT-001 REQ-D-003
     * true 시 생성/수정/삭제/계정/비번재설정 버튼 전체 숨김.
     * MfrStaffManage.vue의 "연관 기공소/병원" 섹션에서 사용.
     * 운영자(XINLAIAN) 경로는 항상 false → 회귀 없음.
     */
    readOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * [2026-08-06] readOnly 와 계정 기능의 분리.
     *
     * 실무자 계정 관리 화면이 이 컴포넌트를 readOnly 로 임베드하는데, readOnly 가
     * 계정관리 버튼까지 숨겨 "계정 관리 화면인데 회사 행만 보이는" 모순이 있었다.
     * 회사 쓰기(생성·수정·삭제)와 계정 관리는 다른 축이므로 따로 제어한다.
     * null(기본) = 종전 동작(!readOnly 를 따름) — 기존 사용처 무영향.
     */
    accountsEnabled: { type: Boolean, default: null },
  },

  data() {
    return {
      companies: [],
      loading: false,
      errorMsg: '',

      // 필터 (REQ-LIST-001)
      filters: {
        addrOne: '',
        medicalDept: '',
        registrationStatus: undefined,
        isTestCompany: undefined,
        name: '',
      },

      // 정렬 — whitelist만 (REQ-LIST-003, REQ-UNW-003)
      sortBy: 'createAt',
      sortOrder: 'desc',

      // 페이지네이션 (backend envelope 소비 — REQ-UBI-002)
      pageSize: 20,
      pagination: {
        current: 1,
        total: 0,
        totalPages: 0,
      },

      // 모바일 감지 (REQ-STATE-005)
      isMobileView: window.innerWidth <= 767,

      // 모달 상태
      formModalOpen: false,
      accountModalOpen: false,
      selectedRecord: null,
    };
  },

  computed: {
    /** 용어 통일: HOSPITAL=병원(医院) / DENTAL_LABORATORY=기공소(技工所) */
    entitySuffix() {
      return this.type === 'DENTAL_LABORATORY' ? '_dental_lab' : '_hospital';
    },
    // @MX:NOTE: [AUTO] actor role은 JWT payload.role에서 읽음 — OD-FE-2 확정
    // REQ-UNW-007: store getter 복사 금지 — jwtRole() 직접 호출
    actorRole() {
      return jwtRole();
    },
    isCertilife() {
      return this.actorRole === 'XINLAIAN';
    },
    isManufacturer() {
      return this.actorRole === 'MANUFACTURER';
    },
    isDentalLab() {
      return this.actorRole === 'DENTAL_LABORATORY';
    },

    // §5 권한 매트릭스: 추가 버튼 표시 여부
    // REQ-D-003: readOnly=true(MANUFACTURER 연관 기공소/병원 조회 전용)이면 false 반환
    //
    // [2026-08-03] 제조사에게 **병원** 추가 버튼이 뜨던 불일치를 고쳤다.
    // 서버는 병원 생성을 운영자·기공소로만 허용한다(chinaCompany.create.hospital,
    // roles=[DENTAL_LABORATORY] + 운영자 선단락). 그런데 여기서는 제조사 전체에 true 를
    // 돌려줘, 버튼을 눌렀다면 저장 단계에서 403 을 만났다. 지금은 딜러 계정에 병원관리
    // 메뉴가 없어 드러나지 않았을 뿐이다 — 메뉴를 주는 순간 바로 보이는 결함이었다.
    //
    // 화면 게이팅은 서버 규칙을 **좁게** 따라간다. 넓게 열어두면 "눌렀는데 안 되는 버튼"이 되고,
    // 그건 권한이 없다는 사실을 저장 시점에야 알려주는 가장 나쁜 UX다.
    canCreate() {
      if (this.readOnly) return false;
      if (this.isCertilife) return true;
      // 제조사: 기공소만 생성 가능 (병원 생성 권한 없음 — 서버 규칙과 일치)
      if (this.isManufacturer) return this.type === 'DENTAL_LABORATORY';
      // 기공소: 병원 추가 가능, 기공소 추가 불가 (REQ-UNW-008)
      if (this.isDentalLab && this.type === 'HOSPITAL') return true;
      return false;
    },

    // 계정관리 버튼 표시 여부
    // REQ-D-003: readOnly=true이면 false 반환
    //
    // [2026-08-03 2차] 계정 관리는 생성 권한과 **다른 축**이다 — 담당 거래처면 관리한다.
    //
    // 1차에서 canCreate 와 같은 모양으로 좁혔는데, hospital.xinlaian.cn 개통으로 병원 계정이
    // 실사용 대상이 되자 어긋났다: 병원 회사는 기공소가 만들지만, 그 병원을 실제 담당하는
    // 딜러가 계정을 못 만든다. 서버도 같은 날 담당 거래처 전체로 개방됐다(멤버십 강제).
    // 목록 자체가 자기 담당 거래처만 보이므로 여기서는 role 만 본다.
    canManageAccounts() {
      // accountsEnabled 명시 시 readOnly 와 무관하게 그 값을 따른다(위 prop 주석 참조)
      if (this.accountsEnabled !== null) return this.accountsEnabled;
      if (this.readOnly) return false;
      if (this.isCertilife || this.isManufacturer) return true;
      if (this.isDentalLab && this.type === 'HOSPITAL') return true;
      return false;
    },

    // [2026-08-03] 삭제도 같은 규칙이다 — **만든 주체가 거둘 수 있어야 한다.**
    // 못 지우면 잘못 만든 거래처가 목록에 영구히 남고 정리는 매번 운영자 몫이 된다.
    // 서버도 동일 범위이며, 담당하지 않는 회사는 서버가 멤버십으로 막는다.
    canDelete() {
      return this.canCreate;
    },

    pageTitle() {
      return this.type === 'HOSPITAL'
        ? this.$t('company_page_title_hospital')
        : this.$t('company_page_title_dental_lab');
    },

    tableColumns() {
      const cols = [
        { title: this.$t(`company_col_name${this.entitySuffix}`), dataIndex: 'name', key: 'name' },
        // [2026-08-06] 계정 수 — "계정 관리인데 회사만 보인다"는 혼선의 직접 해소.
        // BE listCompanies 가 행마다 accountInfo 배열을 이미 조인해 주므로(REQ-JOIN-001) 추가 비용 0.
        // 클릭 = 해당 회사의 계정 모달(계정관리 버튼과 동일 동작).
        { title: this.$t('company_col_accountCount'), key: 'accountCount', width: 70, align: 'center' },
        { title: this.$t('company_col_addrOne'), dataIndex: 'addrOne', key: 'addrOne' },
        { title: this.$t('company_col_medicalDept'), dataIndex: 'medicalDept', key: 'medicalDept',
          customRender: ({ record }) => {
            const depts = record.medicalDept || [];
            if (!depts.length) return '-';
            // 진료과 코드를 로케일 라벨로 변환(미정의 코드는 코드 그대로 표시)
            return depts.map((d) => (this.$te(`company_dept_${d}`) ? this.$t(`company_dept_${d}`) : d)).join(', ');
          },
        },
        { title: this.$t('company_col_registrationStatus'), dataIndex: 'registrationStatus', key: 'registrationStatus',
          customRender: ({ record }) => {
            const s = record.registrationStatus;
            if (!s) return '-';
            // 등록 상태 코드를 로케일 라벨로 변환(미정의 코드는 코드 그대로 표시)
            return this.$te(`company_status_${s}`) ? this.$t(`company_status_${s}`) : s;
          },
        },
        { title: this.$t('company_col_viewManufacturers'), key: 'viewManufacturers' },
      ];

      // 기공소 칩은 병원 목록에만 표시
      if (this.type === 'HOSPITAL') {
        cols.push({ title: this.$t('company_col_viewDentalLabs'), key: 'viewDentalLabs' });
      }

      cols.push({ title: this.$t('company_col_businessLicense'), key: 'businessLicense', width: 130, align: 'center' });
      if (this.type === 'DENTAL_LABORATORY') {
        cols.push({ title: this.$t('company_col_hengjiu'), key: 'hengjiu', width: 110, align: 'center' });
      }
      cols.push({ title: this.$t('company_col_createAt'), key: 'createAt' });
      cols.push({ title: this.$t('company_col_actions'), key: 'actions', width: 240 });
      return cols;
    },
  },

  mounted() {
    this.fetchCompanies();
    // 뷰포트 리사이즈 감지 (REQ-STATE-005, REF: Vue reactive — DOM 조작 금지)
    window.addEventListener('resize', this.onResize);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
  },

  watch: {
    // type prop이 변경될 때 (라우트 재사용 시) 초기화
    type() {
      this.resetFilters();
      this.fetchCompanies();
    },
  },

  methods: {
    onResize() {
      this.isMobileView = window.innerWidth <= 767;
    },

    // §5 수정 버튼 표시 판단 (DENTAL_LAB는 기공소 수정 불가)
    // REQ-D-003: readOnly=true이면 false 반환
    canEdit(/* record */) {
      if (this.readOnly) return false;
      if (this.isCertilife || this.isManufacturer) return true;
      if (this.isDentalLab && this.type === 'HOSPITAL') return true;
      // DENTAL_LAB + 기공소 = 수정 불가 (REQ-UNW-008)
      return false;
    },

    async fetchCompanies() {
      this.loading = true;
      this.errorMsg = '';
      try {
        const params = this.buildQueryParams();
        const res = await listCompanies(this.type, params);
        // @MX:NOTE: [AUTO] REQ-UBI-002: parsePaginatedResponse로 envelope 소비 (키 재정의 금지)
        const parsed = parsePaginatedResponse(res);
        this.companies = parsed.data;
        this.pagination.total = parsed.total;
        this.pagination.current = parsed.page;
        this.pagination.totalPages = parsed.totalPages;
      } catch (err) {
        const apiErr = extractApiError(err);
        this.errorMsg = apiErr.status === 403
          ? this.$t('company_error_403')
          : apiErr.message || this.$t('company_error_fetch_failed');
        this.companies = [];
      } finally {
        this.loading = false;
      }
    },

    buildQueryParams() {
      const params = {
        page: this.pagination.current,
        limit: this.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
      };
      if (this.filters.addrOne) params.addrOne = this.filters.addrOne;
      if (this.filters.medicalDept) params.medicalDept = this.filters.medicalDept;
      if (this.filters.registrationStatus) params.registrationStatus = this.filters.registrationStatus;
      if (this.filters.isTestCompany !== undefined && this.filters.isTestCompany !== null) {
        params.isTestCompany = this.filters.isTestCompany;
      }
      if (this.filters.name) params.name = this.filters.name;
      return params;
    },

    onSearch() {
      this.pagination.current = 1;
      this.fetchCompanies();
    },

    onReset() {
      // REQ-LIST-004: 필터·검색어·정렬 기본값 복원 + 1페이지 재조회
      this.resetFilters();
      this.fetchCompanies();
    },

    resetFilters() {
      this.filters = {
        addrOne: '',
        medicalDept: '',
        registrationStatus: undefined,
        isTestCompany: undefined,
        name: '',
      };
      this.sortBy = 'createAt';
      this.sortOrder = 'desc';
      this.pagination.current = 1;
    },

    onSortChange() {
      this.pagination.current = 1;
      this.fetchCompanies();
    },

    onPageChange(page) {
      this.pagination.current = page;
      this.fetchCompanies();
    },

    onPageSizeChange(current, size) {
      this.pageSize = size;
      this.pagination.current = 1;
      this.fetchCompanies();
    },

    // REQ-ACT-003: ID복사 — _id는 컬럼 미렌더 (REQ-UNW-004)
    async copyId(record) {
      try {
        await navigator.clipboard.writeText(record._id);
        this.$message.success(this.$t('company_copy_id_success'));
      } catch {
        this.$message.error(this.$t('company_copy_id_failed'));
      }
    },

    // REQ-ACT-004: 삭제 (soft-delete)
    // [2026-08-03] 운영자 전용에서 생성 권한과 동일 범위로 확대(canDelete 주석 참조).
    // 서버가 회사와 **소속 계정을 함께** 거두므로, 확인 문구에 그 사실을 밝힌다 —
    // 계정이 같이 죽는다는 걸 모르고 누르면 안 되는 동작이다.
    async onDelete(record) {
      const confirmed = await confirmDialog(
        this.$t('company_delete_confirm_title'),
        this.$t(`company_delete_confirm_text${this.entitySuffix}`, { name: record.name }),
      );
      if (!confirmed) return;

      try {
        await deleteCompany(this.type, record._id);
        this.$message.success(this.$t('company_delete_success'));
        await this.fetchCompanies();
      } catch (err) {
        const apiErr = extractApiError(err);
        this.$message.error(
          apiErr.status === 403
            ? this.$t('company_error_403')
            : apiErr.message || this.$t('company_error_delete_failed'),
        );
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString('zh-CN');
    },

    // 모달 제어
    openFormModal(record) {
      this.selectedRecord = record;
      this.formModalOpen = true;
    },
    closeFormModal() {
      this.formModalOpen = false;
      this.selectedRecord = null;
    },
    onSaved() {
      this.fetchCompanies();
    },
    openAccountModal(record) {
      this.selectedRecord = record;
      this.accountModalOpen = true;
    },
    closeAccountModal() {
      this.accountModalOpen = false;
      this.selectedRecord = null;
    },
  },
};
</script>

<style scoped>
/* 필터 폼 줄바꿈 정렬 — inline form이 wrap될 때 form-item 간 수직 간격 확보 */
/* (Ant inline form은 wrap 시 margin-bottom이 0이라 줄이 겹쳐 보이는 문제 해결) */
.company-filter-form :deep(.ant-form-item) {
  margin-bottom: 12px;
}
.company-filter-form :deep(.ant-form-item:last-child) {
  margin-bottom: 0;
}
/* 검색/초기화 버튼은 항상 한 줄로 (좁은 폭에서 초기화가 아래로 떨어지지 않게) */
.company-filter-form .filter-actions :deep(.ant-space) {
  flex-wrap: nowrap;
}
/* 검색/초기화 버튼을 우측 정렬 (남는 공간을 왼쪽으로 밀어 버튼을 오른쪽 끝으로) */
.company-filter-form .filter-actions {
  margin-left: auto;
  margin-right: 0;
}

/* 모바일 카드뷰 — REQ-STATE-005: @media max-width:767px 한정 */
/* 데스크탑(768px+)은 100% 보존 (REQ-MOBILE-002 정합) */
@media (max-width: 767px) {
  .mobile-card-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .mobile-company-card {
    border: 1px solid #f0f0f0;
    border-radius: 6px;
    padding: 12px;
    background: #fff;
  }

  .card-field {
    display: flex;
    gap: 8px;
    margin-bottom: 6px;
    font-size: 13px;
  }

  .card-label {
    font-weight: 600;
    min-width: 80px;
    color: #666;
  }

  .card-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
    border-top: 1px solid #f5f5f5;
    padding-top: 8px;
  }
}

/* 영업집조(营业执照) 셀 */
.business-license-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.business-license-cell .license-no {
  max-width: 110px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  color: #666;
}
.business-license-cell .license-no-img {
  display: inline-block;
  width: 40px;
  height: 40px;
  line-height: 40px;
  text-align: center;
  color: #ccc;
  background: #fafafa;
  border: 1px dashed #e8e8e8;
  border-radius: 4px;
  font-size: 11px;
}
.business-license-cell .mono { font-family: monospace; }
.business-license-cell .small { font-size: 11px; }

/* 모바일 카드 안 영업집조는 가로 배치 */
.mobile-company-card .card-field .business-license-cell {
  flex-direction: row;
  align-items: center;
  gap: 8px;
}
</style>
